import { useEffect, useRef } from 'react';
import { useQueryClient, useIsMutating } from '@tanstack/react-query';

import { useRealtimeChannel } from '@/hooks/use-realtime-channel';
import { useAuth } from '@/contexts/auth-context';
import { useOrganization } from '@/contexts/organization-context';

import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

const DEBOUNCE_MS = 300;

interface UseNotificationsRealtimeOptions {
  onInsert?: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void;
}

/**
 * Consolidated realtime subscription for all notification queries.
 * - Single Supabase Realtime channel per user (not 2-4 duplicates)
 * - `onPayload` stabilized by `useRealtimeChannel`'s internal `useEffectEvent`
 * - 300ms debounce prevents cascade during bulk operations
 * - `refetchType: 'active'` only refetches mounted queries
 *
 * Mount once at the page level; do NOT mount in individual hooks.
 */
export function useNotificationsRealtime(options: UseNotificationsRealtimeOptions = {}): void {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { currentOrganizationId } = useOrganization();
  const isMutating = useIsMutating({ mutationKey: ['notifications'] });
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useRealtimeChannel({
    channelName: `notifications-${currentOrganizationId}-${user?.id}`,
    subscriptions: [
      {
        event: '*',
        table: 'notifications',
        filter: `recipient_user_id=eq.${user?.id}`,
      },
    ],
    onPayload: (payload) => {
      if (payload.eventType === 'INSERT') {
        options.onInsert?.(payload);
      }
      if (isMutating > 0) {
        return;
      }
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        queryClient
          .invalidateQueries({
            queryKey: ['notifications'],
            refetchType: 'active',
          })
          .catch(() => void 0);
      }, DEBOUNCE_MS);
    },
    onResubscribed: () => {
      queryClient
        .invalidateQueries({ queryKey: ['notifications'], refetchType: 'active' })
        .catch(() => void 0);
    },
    enabled: !!user?.id && !!currentOrganizationId && currentOrganizationId !== 'admin',
  });

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
    };
  }, []);
}
